/* eslint-disable no-param-reassign */
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import classNames from 'classnames';
import Icon, { ICONS, COLORS } from '../../general-components/icon';
import TooltipWrapper, { TTPosition } from '../../general-components/tooltips/tooltip-wrapper';
import { GreenroomState as GS } from '../../../stores/types/types';
import { getOnsiteLocations, getSrtStreams } from '../../../stores/actions/video';
import { LimitedInputViewType } from '../../../stores/types/session';
import {
	Container,
	ContainerHeader,
	ContainerSubHeader,
	ConfigContainer,
	ViewOptionsContainer,
	SubOptions,
	LocationContainer,
	EntryDropdownButton,
	EntryDropdownButtonText,
	EntryDropdown,
	EntryDropdownOption,
	CheckboxButton,
	Button,
	ButtonLabel,
	AnimatedWrapper,
} from './elements';

interface Props {
	setPage: (page: number) => void;
	selectedView: LimitedInputViewType;
	setView: (newView: LimitedInputViewType) => void;
	locationId: number | null;
	setLocationId: (newLocationId: number | null) => void;
	smartCanvas: boolean;
	setSmartCanvas: (enabled: boolean) => void;
	onContinue: () => void;
	isConnected?: boolean;
}

export default function ConferenceViewConfig(props: Props) {
	const {
		selectedView,
		setView,
		locationId,
		setLocationId,
		smartCanvas,
		setSmartCanvas,
		onContinue,
		isConnected,
	} = props;

	const dispatch = useDispatch();

	const token = useSelector((s: GS) => s.SessionReducer.token);
	const meeting = useSelector((s: GS) => s.SessionReducer.meeting);
	const onsiteLocations = useSelector((s: GS) => s.VideoReducer.onsiteLocations);

	const [locationDropdownOpen, setLocationDropdownOpen] = useState<boolean>(false);
	const [initialView, setInitialView] = useState<LimitedInputViewType>(selectedView);
	const [initialLocationId, setInitialLocationId] = useState<number | null>(locationId);
	const [initialSmartCanvas, setInitialSmartCanvas] = useState<boolean>(smartCanvas);

	// Store initial values when component mounts or when isConnected changes to true
	useEffect(() => {
		if (isConnected) {
			setInitialView(selectedView);
			setInitialLocationId(locationId);
			setInitialSmartCanvas(smartCanvas);
		}
	}, [isConnected]);

	const hasChanges = useMemo(() => {
		if (!isConnected) return true;
		return (
			initialView !== selectedView
			|| initialLocationId !== locationId
			|| initialSmartCanvas !== smartCanvas
		);
	}, [
		isConnected,
		initialView,
		initialLocationId,
		initialSmartCanvas,
		selectedView,
		locationId,
		smartCanvas,
	]);

	const handleClick = () => {
		setLocationDropdownOpen(false);
		window.removeEventListener('click', handleClick);
	};

	const handleOpenDropdown = () => {
		if (!locationDropdownOpen) {
			setLocationDropdownOpen(true);
			setTimeout(() => {
				window.addEventListener('click', handleClick);
			}, 100);
		}
	};

	useEffect(() => {
		if (token && meeting) {
			dispatch(getOnsiteLocations(token, meeting.meeting));
			dispatch(getSrtStreams(token, meeting.meeting));
		}
	}, [token, meeting, dispatch]);

	const selectedLocation = useMemo(() => {
		if (locationId === null) return null;
		return onsiteLocations.find((location) => location.id === locationId) || null;
	}, [locationId, onsiteLocations]);

	return (
		<Container>
			<ContainerHeader className='rainbow-product-acc-text-background'>
				Greenroom for conference rooms
			</ContainerHeader>
			<div className="flex gap-small">
				<ContainerSubHeader>
					How is your room configured?
				</ContainerSubHeader>
				<TooltipWrapper message="Currently available for Cisco conference room solutions" position={TTPosition.right}>
					<div className="info-i-tooltip">
						<Icon name={ICONS.INFO_CIRCLE} size={12} color={COLORS.WHITE} />
					</div>
				</TooltipWrapper>
			</div>
			<ConfigContainer>
				<ViewOptionsContainer>
					<div
						className={classNames('app-entry-viewer-select-button', {selected: selectedView === 'canvas'})}
						onClick={() => setView('canvas')}
					>
						<Icon name={ICONS.MONITOR_CANVAS} size={120} color={COLORS.TEXT_GRAY_100} />
						<h3>Live audience</h3>
						<p>Show canvas on screen</p>
					</div>
					<div
						className={classNames('app-entry-viewer-select-button', {selected: selectedView === 'presenter'})}
						onClick={() => setView('presenter')}
					>
						<Icon name={ICONS.MONITOR_PRESENTER} size={120} color={COLORS.TEXT_GRAY_100} />
						<h3>Presenters only</h3>
						<p>Show presenter interface on screen</p>
					</div>
				</ViewOptionsContainer>
				<SubOptions>
					<AnimatedWrapper className={classNames({ visible: selectedView === 'canvas' })}>
						<LocationContainer>
							<EntryDropdownButton
								onClick={handleOpenDropdown}
								className={classNames({
									open: locationDropdownOpen,
								})}
							>
								<EntryDropdownButtonText className={selectedLocation ? 'selected' : ''}>
									{selectedLocation?.locationName || 'Add a location here'}
								</EntryDropdownButtonText>
								<div className="flex-center">
									<Icon
										name={locationDropdownOpen ? ICONS.CARROT_UP : ICONS.CARROT_DOWN}
										size={12}
										color={COLORS.WHITE}
									/>
								</div>
							</EntryDropdownButton>
						</LocationContainer>
						<CheckboxButton onClick={() => setSmartCanvas(!smartCanvas)}>
							<input
								type="checkbox"
								checked={smartCanvas}
								readOnly
							/>
							<ButtonLabel>Smart Canvas</ButtonLabel>
						</CheckboxButton>
					</AnimatedWrapper>
					<EntryDropdown
						className={classNames(locationDropdownOpen && 'open')}
						// eslint-disable-next-line consistent-return
						ref={(el) => {
							if (!el) return;
							const vh = Math.max(
								document.documentElement.clientHeight || 0,
								window.innerHeight || 0,
							);
							const rect = el.getBoundingClientRect();
							const bottomSpace = vh - rect.top;
							el.style.maxHeight = `${bottomSpace - 20}px`; // 20px buffer
							el.style.overflowY = 'auto';
						}}
					>
						<EntryDropdownOption className="header">
							<span className="bl-font-l3">LOCATIONS</span>
						</EntryDropdownOption>
						{onsiteLocations.length > 0 ? (
							<EntryDropdownOption onMouseUp={() => setLocationId(null)}>
								<span className="bl-font-p2 truncate-overflow">None</span>
								{!locationId && (
									<div className="flex-center">
										<Icon name={ICONS.CHECK} size={10} color={COLORS.WHITE} />
									</div>
								)}
							</EntryDropdownOption>
						) : (
							<EntryDropdownOption className="no-locations">
								<span className="bl-font-p2 truncate-overflow semantic-red-500">
									No locations added to this greenroom
								</span>
							</EntryDropdownOption>
						)}
						{onsiteLocations.map((location, index) => (
							<EntryDropdownOption
								key={index}
								onMouseUp={() => setLocationId(location.id)}
							>
								<span className="bl-font-p2 truncate-overflow">{location.locationName}</span>
								{locationId === location.id && (
									<div className="flex-center">
										<Icon name={ICONS.CHECK} size={10} color={COLORS.WHITE} />
									</div>
								)}
							</EntryDropdownOption>
						))}
					</EntryDropdown>
				</SubOptions>
				<Button
					className={classNames("sherbert", {disabled: selectedView === 'none' || (isConnected && !hasChanges)})}
					onClick={onContinue}
					style={{ marginTop: 0 }}
				>
					<ButtonLabel className="grayscale-1200">
						{isConnected ? 'Apply Changes' : 'Connect room'}
					</ButtonLabel>
				</Button>
				<Button
					className="link transparent"
					onClick={() => {
						window.open('https://support.brandlive.com/hc/en-us/articles/31259261433243-How-to-Join-Greenroom-via-Conference-Room', '_blank');
					}}
				>
					Learn More
					<Icon name={ICONS.EXPAND} size={12} color={COLORS.TEXT_GRAY_200} />
				</Button>
			</ConfigContainer>
		</Container>
	);
}
